import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2, ExternalLink, Shield, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

const SHOOT_TYPES = [
    { value: "Property/Estate", label: "Property / Estate" },
    { value: "Event", label: "Event" },
    { value: "Documentary/Promotional", label: "Documentary / Promotional" },
    { value: "Other", label: "Other" },
];

const PORTFOLIO_URL = "https://drive.google.com/drive/folders/16sGJGJf13UNclwXUJdMYK8HOW96Ytn2B";
const CONTACT_EMAIL = "mireland028@gmail.com";

export default function CommercialEnquiries() {
    const [form, setForm] = useState({
        name: "", company: "", email: "", phone: "",
        shoot_type: "Property/Estate", description: "", preferred_date: "",
    });
    const [busy, setBusy] = useState(false);
    const [submittedRef, setSubmittedRef] = useState("");

    function update(key, val) { setForm((f) => ({ ...f, [key]: val })); }

    async function submit(e) {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.post("/commercial-enquiries", form);
            setSubmittedRef(r.data?.id || "");
            toast.success("Enquiry sent — I'll reply within 48 hours.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    }

    // Field helper — inline, styled to match the rest of the site's inputs.
    const inputCls = "w-full bg-transparent border border-[var(--sb-border)] px-4 py-3 text-[var(--sb-text)] placeholder:text-[var(--sb-text-muted)] focus:outline-none focus:border-[var(--sb-brand)] transition-colors";

    return (
        <div className="relative overflow-hidden">
            <HighlandBackdrop variant="soft" />

            <section className="relative max-w-5xl mx-auto px-6 lg:px-12 pt-24 lg:pt-32 pb-16">
                <Reveal>
                    <div className="overline mb-4" style={{ color: "#5A9BD8", fontWeight: 700 }}>
                        DVS Cinematography
                    </div>
                </Reveal>
                <MaskedLine>
                    <h1 className="font-serif font-light tracking-tighter leading-[0.95] mb-8" style={{ fontSize: "clamp(2.4rem, 7vw, 5.5rem)" }} data-testid="commercial-headline">
                        Commercial Enquiries
                    </h1>
                </MaskedLine>
                <Reveal delay={0.3}>
                    <p className="text-base md:text-lg text-[var(--sb-text-dim)] leading-relaxed max-w-2xl">
                        DVS Cinematography provides professional aerial drone footage for property listings, estate
                        agents, land surveys, events, and commercial projects across <b className="text-[var(--sb-text)]">Perthshire
                        and Central Scotland</b>. CAA compliant, fully insured, operated by Mark Ireland.
                    </p>
                </Reveal>

                <Reveal delay={0.5}>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--sb-border)] text-xs uppercase tracking-[0.22em] text-[var(--sb-text-dim)]">
                            <Shield size={13} /> CAA compliant
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--sb-border)] text-xs uppercase tracking-[0.22em] text-[var(--sb-text-dim)]">
                            <Shield size={13} /> Fully insured
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--sb-border)] text-xs uppercase tracking-[0.22em] text-[var(--sb-text-dim)]">
                            <Camera size={13} /> 4K aerial · edit included
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* Form */}
            <section className="relative max-w-3xl mx-auto px-6 lg:px-12 pb-24">
                {submittedRef ? (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative border p-8 md:p-12 text-center"
                        style={{ borderColor: "rgba(107,167,222,0.4)", background: "linear-gradient(135deg, rgba(0,85,164,0.12) 0%, rgba(20,42,77,0.4) 100%)" }}
                        data-testid="commercial-success"
                    >
                        <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t border-l" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t border-r" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b border-l" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b border-r" style={{ borderColor: "#0055A4" }} />
                        <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: "#5A9BD8" }} />
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-3">Thanks, I&rsquo;ve got it.</h2>
                        <p className="text-[var(--sb-text-dim)] leading-relaxed mb-2">
                            I&rsquo;ll come back to you within 48 hours with next steps and a quote.
                        </p>
                        <p className="text-xs uppercase tracking-[0.28em] text-[var(--sb-text-muted)] mt-4">
                            Reference · {submittedRef}
                        </p>
                    </motion.div>
                ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="commercial-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Your name*</label>
                                <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} placeholder="Jane Smith" data-testid="commercial-name" />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Company</label>
                                <input value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} placeholder="Optional" data-testid="commercial-company" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Email*</label>
                                <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@example.com" data-testid="commercial-email" />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Phone</label>
                                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="Optional" data-testid="commercial-phone" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Type of shoot*</label>
                                <select value={form.shoot_type} onChange={(e) => update("shoot_type", e.target.value)} className={inputCls} data-testid="commercial-shoot-type" style={{ colorScheme: "dark" }}>
                                    {SHOOT_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Preferred date</label>
                                <input value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className={inputCls} placeholder="e.g. 15 Sept 2026 or flexible" data-testid="commercial-preferred-date" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mb-2">Brief description of requirements*</label>
                            <textarea required rows={6} value={form.description} onChange={(e) => update("description", e.target.value)} className={inputCls} placeholder="A few lines about the project — location, angles, deliverables, any deadlines." data-testid="commercial-description" />
                        </div>
                        <div className="pt-2">
                            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 w-full md:w-auto justify-center" data-testid="commercial-submit">
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Send enquiry</>}
                            </button>
                            <p className="text-xs text-[var(--sb-text-muted)] mt-3">
                                I&rsquo;ll reply within 48 hours with next steps and a fair quote.
                            </p>
                        </div>
                    </form>
                )}

                {/* Portfolio + contact ---------------------------------------- */}
                <div className="mt-16 border-t border-[var(--sb-border)] pt-10">
                    <div className="overline mb-3" style={{ color: "#5A9BD8", fontWeight: 700 }}>Prefer to browse first?</div>
                    <p className="text-[var(--sb-text-dim)] leading-relaxed mb-4">
                        Alternatively, view our portfolio of previous commercial work:
                    </p>
                    <a
                        href={PORTFOLIO_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="btn-outline inline-flex items-center gap-2 break-all"
                        data-testid="commercial-portfolio-link"
                    >
                        <ExternalLink size={14} /> View portfolio on Google Drive
                    </a>

                    <div className="mt-10 text-sm text-[var(--sb-text-dim)]" data-testid="commercial-contact-footer">
                        Direct contact: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--sb-brand)] hover:text-white underline">{CONTACT_EMAIL}</a>
                    </div>
                </div>
            </section>
        </div>
    );
}
